import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaChevronUp, FaGlobe } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import classes from "./LanguageSelector.module.css";
import { languages } from "../utils/constants";
import { setLanguage } from "../store/slices/languageSlice";

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const selectedLanguageId = Number(
    useSelector((state) => state?.persisted?.languageSlice?.language ?? 1)
  );

  const selectedLanguage =
    languages.find(
      (lang) => Number(lang.language_id) === Number(selectedLanguageId)
    ) || languages[0];

  const handleLanguageChange = (lang) => {
    const langId = Number(lang.language_id);

    dispatch(setLanguage(langId));
    i18n.changeLanguage(String(langId));
    setLangOpen(false);
  };

  useEffect(() => {
    const activeLanguage =
      languages.find(
        (lang) => Number(lang.language_id) === Number(selectedLanguageId)
      ) || languages[0];

    const i18nCurrentLanguage = String(i18n.resolvedLanguage || i18n.language || "1");

    if (String(activeLanguage.language_id) !== i18nCurrentLanguage) {
      i18n.changeLanguage(String(activeLanguage.language_id));
    }
  }, [selectedLanguageId, i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={classes.languageFabWrap} ref={langRef}>
      <AnimatePresence>
        {langOpen && (
          <div className={classes.languageOptions}>
            {languages.map((lang, index) => {
              const isSelected =
                Number(lang.language_id) === Number(selectedLanguageId);

              return (
                <motion.button
                  key={lang.language_id}
                  type="button"
                  className={`${classes.languageOption} ${
                    isSelected ? classes.languageOptionActive : ""
                  }`}
                  onClick={() => handleLanguageChange(lang)}
                  initial={{ opacity: 0, y: 18, scale: 0.88 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 18, scale: 0.88 }}
                  transition={{ duration: 0.2, delay: index * 0.04 }}
                >
                  <span className={classes.languageOptionShort}>
                    {lang.short}
                  </span>
                  <span className={classes.languageOptionLabel}>
                    {lang.label}
                  </span>
                </motion.button>
              );
            })}
          </div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        className={`${classes.languageMainFab} ${
          langOpen ? classes.languageMainFabOpen : ""
        }`}
        onClick={() => setLangOpen((prev) => !prev)}
        whileTap={{ scale: 0.96 }}
      >
        <span className={classes.languageMainIcon}>
          <FaGlobe />
        </span>

        <span className={classes.languageMainText}>
          {selectedLanguage.label}
        </span>

        <span
          className={`${classes.languageMainArrow} ${
            langOpen ? classes.languageMainArrowOpen : ""
          }`}
        >
          <FaChevronUp />
        </span>
      </motion.button>
    </div>
  );
};

export default LanguageSelector;